import { useContext, useEffect, useState } from 'react';

import Loading from '../../../pages/Loading';
import { usePrevious } from '../../../utils/usePrevious';
import { UserRoles } from '../../roster/types';
import { WhiteboardContext } from '../context/WhiteboardContext';
import { WhiteboardSessionContext } from '../context/WhiteboardSessionContext';
import { useWhiteboardPageNavigation } from '../hooks';
import { PageActions } from '../state/WhiteboardSessionState';
import { type Page, SessionTypes } from '../types';
import WhiteboardContent from '../WhiteboardContent';
import { WhiteboardLayout } from '../WhiteboardLayout';

/**
 * Displays all whiteboard view components. This component handles UI at the session level (things that exist across
 * pages). Acts are a wrapper for ExcalidrawController which handles one page at a time.
 */
export const ExcalidrawSession = () => {
    const { classUuid, userInfo, joinWhiteboardChannel, leaveWhiteboardChannel } = useContext(WhiteboardContext);

    const {
        socketState,
        whiteboardSessionState,
        dispatchWhiteboardSessionAction,
        sessionId,
        changePage,
        persist,
        excalidrawAPIRef,
        setCanvasLoading,
        isInControl,
        setIsInControl
    } = useContext(WhiteboardSessionContext);

    const { presenter, currentPage, session, pages, userPage, breakoutPage } = whiteboardSessionState;

    const [requestedPage, setRequestedPage] = useState<Page | undefined>();
    const prevPage = usePrevious(currentPage);

    const { onNextPageHandler, onPrevPageHandler } = useWhiteboardPageNavigation(setRequestedPage);

    useEffect(() => {
        if (requestedPage) {
            persist();
            changePage(requestedPage);
            setRequestedPage(undefined);
        }
    }, [requestedPage, changePage, whiteboardSessionState, persist]);

    /**
     * If someone is presenting take the user to the presenter's page
     */
    useEffect(() => {
        if (!userInfo || !presenter || !currentPage) return;

        if (
            presenter.student_id !== userInfo.student_id && // User is NOT the presenter.
            presenter.currentPageId !== currentPage.page_id // User's page is NOT the presenter's page.
        ) {
            const presenterPage = pages.find((p) => p.page_id === presenter.currentPageId);
            if (presenterPage) {
                dispatchWhiteboardSessionAction({
                    type: PageActions.SetCurrentPage,
                    payload: { page: presenterPage! }
                });
            }
        }

        setIsInControl(presenter.student_id === userInfo.student_id);
    }, [dispatchWhiteboardSessionAction, currentPage, pages, userPage, presenter, userInfo, setIsInControl]);

    /**
     * Sends students back to individual pages after
     * presentation is over.
     */
    useEffect(() => {
        if (presenter) return;
        if (session?.type !== SessionTypes.Individual) return;

        if (userInfo?.role === UserRoles.Student && userPage && currentPage?.page_id !== userPage.page_id) {
            dispatchWhiteboardSessionAction({ type: PageActions.SetCurrentPage, payload: { page: userPage } });
        }
    }, [pages, session, userInfo, userPage, presenter, currentPage?.page_id, dispatchWhiteboardSessionAction]);

    /**
     * Sends students back to breakout pages after
     * presentation is over.
     */
    useEffect(() => {
        if (presenter) return;
        if (session?.type !== SessionTypes.Breakout) return;

        if (userInfo?.role === UserRoles.Student && breakoutPage && currentPage?.page_id !== breakoutPage.page_id) {
            dispatchWhiteboardSessionAction({ type: PageActions.SetCurrentPage, payload: { page: breakoutPage } });
        }
    }, [pages, session, userInfo, breakoutPage, presenter, currentPage?.page_id, dispatchWhiteboardSessionAction]);

    /**
     * Subscribe to session and class channels
     * for session and class related messages.
     */
    useEffect(() => {
        if (socketState.connectionStatus !== 'connected') return;
        if (!socketState.socket?.sessionId) return;

        joinWhiteboardChannel(socketState.socket.sessionId, { session_ids: [sessionId], class_id: classUuid });
    }, [classUuid, sessionId, socketState.socket, joinWhiteboardChannel, socketState.connectionStatus]);

    /**
     * Subscribe to events on the current page when switching pages
     */
    useEffect(() => {
        const connectToPage = async () => {
            if (socketState.connectionStatus !== 'connected') return;
            if (!socketState.socket?.sessionId) return;
            if (!currentPage) return;
            if (prevPage?.page_id === currentPage.page_id) return;

            let pagesToLeave: string[] = [];
            const pagesToJoin: string[] = [currentPage.page_id];

            pages.forEach((page) => {
                pagesToLeave.push(page.page_id);
            });

            pages.forEach((page) => {
                if (page.type === 'template' && pagesToJoin.indexOf(page.page_id) === -1) {
                    pagesToJoin.push(page.page_id);
                }
                if (page.type === 'template' && pagesToLeave.indexOf(page.page_id) !== -1) {
                    pagesToLeave = pagesToLeave.filter((p) => p !== page.page_id);
                }
            });

            pagesToJoin.forEach((pageId) => {
                pagesToLeave = pagesToLeave.filter((p) => p !== pageId);
            });

            setCanvasLoading(true);

            if (pagesToLeave.length > 0) {
                await leaveWhiteboardChannel(socketState.socket!.sessionId, { page_ids: pagesToLeave });
            }
            await joinWhiteboardChannel(socketState.socket!.sessionId, { page_ids: pagesToJoin });

            setCanvasLoading(false);
        };

        connectToPage();
    }, [
        currentPage,
        pages,
        presenter,
        prevPage?.page_id,
        socketState.socket,
        joinWhiteboardChannel,
        leaveWhiteboardChannel,
        socketState.connectionStatus,
        isInControl,
        userInfo,
        excalidrawAPIRef,
        setCanvasLoading,
        session
    ]);

    if (!whiteboardSessionState.currentPage || !whiteboardSessionState.session || !userInfo) {
        return <Loading />;
    }

    return (
        <WhiteboardLayout onNextPage={onNextPageHandler} onPrevPage={onPrevPageHandler}>
            <WhiteboardContent />
        </WhiteboardLayout>
    );
};
